import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { useHydrated } from 'remix-utils/use-hydrated'
import {
	REMOVE_ADDRESS_FORM,
	REMOVE_ADDRESS_INPUTS,
	removeAddressSchema,
} from '#app/components/forms/remove-address-form/remove-address-form.tsx'
import { type GetAddress } from '#app/models/ecommerce-providers/sylius/shop/fetch/address/get-address.ts'
import { PROFILE_ADDRESSES_INDEX_I18NEXT } from '#app/routes/($lang).profile.addresses._index/route-config'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseRemoveAddressProps = {
	id: GetAddress['id']
}

function useRemoveAddressForm({ id }: UseRemoveAddressProps) {
	const { t } = useTranslation(
		PROFILE_ADDRESSES_INDEX_I18NEXT.routeProfileAddressesIndex,
	)
	const isHydrated = useHydrated()

	const fetcherKey = REMOVE_ADDRESS_FORM.generateFetcherKey!({
		id: String(id),
	})
	const fetcher = useFetcher({
		key: fetcherKey,
	})
	const formValues = {
		[ROOT_INPUTS.subaction]: REMOVE_ADDRESS_FORM.subaction,
		[ROOT_INPUTS.noJs]: String(!isHydrated),
		[REMOVE_ADDRESS_INPUTS.removeAddress]: id,
	}
	const [form, fields] = useForm({
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: removeAddressSchema,
			})
		},
		shouldValidate: 'onSubmit',
	})

	return {
		removeAddressFetcherKey: fetcherKey,
		removeAddressFetcher: fetcher,
		removeAddressFormValues: formValues,
		removeAddressForm: form,
		removeAddressFields: fields,
		t,
	}
}

export { type UseRemoveAddressProps, useRemoveAddressForm }
